import React, { useEffect, useState } from "react";
import { AppTextField, AppPasswordField } from "components/common/inputs";
import { AppDialog } from "components/common/dialogs";
import {
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Box,
} from "@mui/material";
import { registerUser } from "../../services/user.service";
import PersonnelPickerDialog from "./PersonnelPickerDialog";
import "./UserModals.css";
import { AppButton } from "components/common/buttons";

const AddUserModal = ({ open, onClose, onUserAdded }) => {
  const [formData, setFormData] = useState({
    userName: "",
    password: "",
    confirmPassword: "",
    fullName: "",
    email: "",
    personnelId: null,
    isActive: false,
  });

  const [selectedPersonnel, setSelectedPersonnel] = useState(null);
  const [personnelPickerOpen, setPersonnelPickerOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open) {
      setPersonnelPickerOpen(false);
    }
  }, [open]);

  const handlePersonnelSelect = (personnel) => {
    setSelectedPersonnel(personnel);
    setPersonnelPickerOpen(false);
    setError(null);
    setFormErrors((previous) => ({ ...previous, email: null }));
    setFormData((prev) => ({
      ...prev,
      personnelId: personnel?.personnelId || null,
      fullName: personnel?.fullName || "",
      email: personnel?.email || "",
      isActive: personnel ? prev.isActive : false,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null,
      });
    }
  };

  const handleSwitchChange = (e) => {
    if (e.target.checked && !formData.personnelId) {
      setError("Chưa gán nhân sự nên không kích hoạt được tài khoản.");
      setFormData({
        ...formData,
        isActive: false,
      });
      return;
    }

    setFormData({
      ...formData,
      [e.target.name]: e.target.checked,
    });
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.userName || formData.userName.trim() === "") {
      errors.userName = "Tên đăng nhập không được để trống";
    }

    if (!formData.password || formData.password.length < 6) {
      errors.password = "Mật khẩu phải có ít nhất 6 ký tự";
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Mật khẩu xác nhận không khớp";
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email không hợp lệ";
    }

    return errors;
  };

  const handleSubmit = async () => {
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await registerUser({
        userName: formData.userName,
        password: formData.password,
        confirmPassword: formData.confirmPassword, // Thêm trường confirmPassword
        fullName: formData.fullName || null,
        email: formData.email || null,
        personnelId: formData.personnelId || null,
        isActive: Boolean(formData.personnelId) && formData.isActive,
      });

      setSuccess(true);

      // Reset form after successful submission
      setTimeout(() => {
        setSelectedPersonnel(null);
        setFormData({
          userName: "",
          password: "",
          confirmPassword: "",
          fullName: "",
          email: "",
          personnelId: null,
          isActive: false,
        });
        setSuccess(false);
        onUserAdded();
        onClose();
      }, 1500);
    } catch (err) {
      console.error("Error registering user:", err);
      if (err.response && err.response.data) {
        setError(
          err.response.data.message || "Có lỗi xảy ra khi tạo người dùng."
        );
      } else {
        setError("Có lỗi xảy ra khi tạo người dùng. Vui lòng thử lại sau.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedPersonnel(null);
    setPersonnelPickerOpen(false);
    // Reset form and errors on close
    setFormData({
      userName: "",
      password: "",
      confirmPassword: "",
      fullName: "",
      email: "",
      personnelId: null,
      isActive: false,
    });
    setFormErrors({});
    setError(null);
    setSuccess(false);
    onClose();
  };

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      title="Thêm người dùng mới"
      size="sm"
      loading={isSubmitting}
      actions={
        <>
          <AppButton variant="outlined" color="inherit" onClick={handleClose} disabled={isSubmitting}>
            Hủy
          </AppButton>
          <AppButton
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting || success}
          >
            {isSubmitting ? <CircularProgress size={24} /> : "Tạo người dùng"}
          </AppButton>
        </>
      }
    >
        {error && (
          <Alert severity="error" className="alert-margin">
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" className="alert-margin">
            Tạo người dùng thành công!
          </Alert>
        )}

        <div className="form-row">
          <AppTextField
            name="userName"
            label="Tên đăng nhập"
            value={formData.userName}
            onChange={(_, event) => handleChange(event)}
            fullWidth
            error={!!formErrors.userName}
            helperText={formErrors.userName}
            disabled={isSubmitting || success}
            margin="normal"
            required
          />
        </div>

        <div className="form-row form-row-2-col">
          <AppPasswordField
            name="password"
            label="Mật khẩu"
            type="password"
            value={formData.password}
            onChange={(_, event) => handleChange(event)}
            error={!!formErrors.password}
            helperText={formErrors.password}
            disabled={isSubmitting || success}
            margin="normal"
            required
          />

          <AppPasswordField
            name="confirmPassword"
            label="Xác nhận mật khẩu"
            type="password"
            value={formData.confirmPassword}
            onChange={(_, event) => handleChange(event)}
            error={!!formErrors.confirmPassword}
            helperText={formErrors.confirmPassword}
            disabled={isSubmitting || success}
            margin="normal"
            required
          />
        </div>

        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 2 }}>
          <AppTextField
            label="Nhân sự liên kết"
            value={selectedPersonnel?.fullName || ""}
            placeholder="Chưa chọn nhân sự"
            InputProps={{ readOnly: true }}
            disabled={isSubmitting || success}
            sx={{ flex: 1 }}
            helperText={
                  !formData.personnelId
                    ? "Chưa chọn nhân sự thì tài khoản không thể kích hoạt."
                    : ""
                }
          />
          <AppButton variant="outlined" onClick={() => setPersonnelPickerOpen(true)}
            disabled={isSubmitting || success} sx={{ flexShrink: 0 }}>
            Chọn nhân sự
          </AppButton>
          {selectedPersonnel && <AppButton size="small" color="inherit" onClick={() => handlePersonnelSelect(null)}
          disabled={isSubmitting || success}>Bỏ liên kết</AppButton>}
        </Box>
        {open && personnelPickerOpen && <PersonnelPickerDialog
          selectedPersonnelId={formData.personnelId}
          onClose={() => setPersonnelPickerOpen(false)} onSelect={handlePersonnelSelect} />}

        <div className="form-row">
          <AppTextField
            name="fullName"
            label="Tên người dùng"
            value={formData.fullName}
            onChange={(_, event) => handleChange(event)}
            fullWidth
            disabled={isSubmitting || success}
            margin="normal"
            helperText="Tự động lấy theo nhân sự liên kết, có thể chỉnh sửa"
          />
        </div>

        <div className="form-row">
          <AppTextField
            name="email"
            label="Email"
            type="email"
            value={formData.email}
            onChange={(_, event) => handleChange(event)}
            fullWidth
            error={!!formErrors.email}
            helperText={formErrors.email || "Tự động lấy theo nhân sự liên kết, có thể chỉnh sửa"}
            disabled={isSubmitting || success}
            margin="normal"
          />
        </div>

        <div className="form-row">
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={handleSwitchChange}
                name="isActive"
                color="primary"
                disabled={isSubmitting || success || !formData.personnelId}
              />
            }
            label="Kích hoạt tài khoản"
          />
        </div>
    </AppDialog>
  );
};

export default AddUserModal;
